import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, ToggleLeft, ToggleRight, Send, ChevronDown, ChevronUp, Clock, CheckCircle, XCircle, Copy, Eye, EyeOff } from 'lucide-react';
import Layout from '../components/Layout';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AVAILABLE_EVENTS = [
  { id: 'order.paid', label: 'Orden Pagada', desc: 'Se dispara cuando una orden es pagada exitosamente' },
  { id: 'order.expired', label: 'Orden Expirada', desc: 'Se dispara cuando una orden expira sin ser pagada' },
  { id: 'order.failed', label: 'Orden Fallida', desc: 'Se dispara cuando falla el procesamiento de una orden' },
  { id: 'order.cancelled', label: 'Orden Cancelada', desc: 'Se dispara cuando una orden es cancelada' },
];

function WebhookCard({ webhook, onToggle, onDelete, onTest, onViewDeliveries }) {
  const [showSecret, setShowSecret] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);
  const [testing, setTesting] = useState(false);

  const isActive = webhook.active || webhook.status === 'active';
  const secret = webhook.secret || webhook.secret_key || '';

  const loadDeliveries = async () => {
    if (deliveries.length > 0) {
      setExpanded(!expanded);
      return;
    }
    setLoadingDeliveries(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BACKEND_URL}/api/webhooks/${webhook.webhook_id}/deliveries?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeliveries(res.data);
      setExpanded(true);
    } catch (err) {
      console.error('Error loading deliveries:', err);
    } finally {
      setLoadingDeliveries(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await onTest(webhook.webhook_id);
      // Reload deliveries after test
      setTimeout(async () => {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${BACKEND_URL}/api/webhooks/${webhook.webhook_id}/deliveries?limit=20`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDeliveries(res.data);
        setExpanded(true);
      }, 2000);
    } finally {
      setTesting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div data-testid={`webhook-card-${webhook.webhook_id}`} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                {isActive ? 'Activo' : 'Inactivo'}
              </span>
              {webhook.project_name && (
                <span className="text-xs text-gray-500 bg-gray-50 px-2 py-0.5 rounded">{webhook.project_name}</span>
              )}
            </div>
            <p className="text-sm font-mono text-gray-800 truncate" title={webhook.url || webhook.webhook_url}>
              {webhook.url || webhook.webhook_url}
            </p>
          </div>
          <div className="flex items-center gap-1 ml-3">
            <button
              data-testid={`toggle-webhook-${webhook.webhook_id}`}
              onClick={() => onToggle(webhook.webhook_id)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title={isActive ? 'Desactivar' : 'Activar'}
            >
              {isActive ? <ToggleRight className="w-5 h-5 text-green-600" /> : <ToggleLeft className="w-5 h-5 text-gray-400" />}
            </button>
            <button
              data-testid={`test-webhook-${webhook.webhook_id}`}
              onClick={handleTest}
              disabled={testing || !isActive}
              className="p-2 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-40"
              title="Enviar prueba"
            >
              <Send className={`w-4 h-4 ${testing ? 'animate-pulse text-blue-500' : 'text-blue-600'}`} />
            </button>
            <button
              data-testid={`delete-webhook-${webhook.webhook_id}`}
              onClick={() => onDelete(webhook.webhook_id)}
              className="p-2 rounded-lg hover:bg-red-50 transition-colors"
              title="Eliminar"
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </button>
          </div>
        </div>

        {/* Events */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {(webhook.events || []).map((ev) => (
            <span key={ev} className="text-xs px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 font-medium">
              {ev}
            </span>
          ))}
        </div>

        {/* Secret */}
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span className="font-medium">Secret:</span>
          <code className="font-mono bg-gray-50 px-2 py-0.5 rounded flex-1 truncate">
            {showSecret ? secret : '••••••••••••••••'}
          </code>
          <button onClick={() => setShowSecret(!showSecret)} className="p-1 hover:bg-gray-100 rounded">
            {showSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
          <button onClick={() => copyToClipboard(secret)} className="p-1 hover:bg-gray-100 rounded" title="Copiar">
            <Copy className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Delivery Stats */}
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
          <span>Total: {webhook.total_deliveries || 0}</span>
          <span className="text-green-600">Exitosos: {webhook.successful_deliveries || 0}</span>
          <span className="text-red-500">Fallidos: {webhook.failed_deliveries || 0}</span>
          <button
            data-testid={`view-deliveries-${webhook.webhook_id}`}
            onClick={loadDeliveries}
            className="ml-auto flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium"
          >
            {loadingDeliveries ? 'Cargando...' : 'Entregas'}
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Deliveries */}
      {expanded && (
        <div className="border-t border-gray-200 bg-gray-50 max-h-64 overflow-y-auto">
          {deliveries.length === 0 ? (
            <p className="text-sm text-gray-500 p-4 text-center">Sin entregas registradas</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Evento</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Estado</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">HTTP</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Intentos</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.map((d) => (
                  <tr key={d.delivery_id} className="border-t border-gray-100">
                    <td className="px-4 py-2 font-mono">{d.event}</td>
                    <td className="px-4 py-2">
                      {d.success || d.status === 'delivered' ? (
                        <span className="flex items-center gap-1 text-green-600"><CheckCircle className="w-3 h-3" /> OK</span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-500"><XCircle className="w-3 h-3" /> Fallido</span>
                      )}
                    </td>
                    <td className="px-4 py-2">{d.status_code || d.http_status_code || '-'}</td>
                    <td className="px-4 py-2">{d.attempts}</td>
                    <td className="px-4 py-2 text-gray-500">
                      {d.created_at ? new Date(d.created_at).toLocaleString('es-VE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function CreateWebhookForm({ projects, onCreated, onCancel }) {
  const [url, setUrl] = useState('');
  const [projectId, setProjectId] = useState(projects[0]?.project_id || '');
  const [events, setEvents] = useState(['order.paid']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const toggleEvent = (eventId) => {
    setEvents((prev) => prev.includes(eventId) ? prev.filter((e) => e !== eventId) : [...prev, eventId]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!url || !projectId || events.length === 0) {
      setError('Completa todos los campos y selecciona al menos un evento');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/webhooks?project_id=${projectId}`, { url, events }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onCreated();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear webhook');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
      <h3 className="text-lg font-bold text-gray-800">Nuevo Webhook</h3>

      {error && <div data-testid="webhook-create-error" className="bg-red-50 text-red-700 text-sm p-3 rounded-lg">{error}</div>}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Proyecto *</label>
        <select
          data-testid="webhook-project-select"
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          required
        >
          {projects.map((p) => (
            <option key={p.project_id} value={p.project_id}>{p.project_name}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">URL del Webhook *</label>
        <input
          data-testid="webhook-url-input"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="https://tu-servidor.com/webhook"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Eventos *</label>
        <div className="grid grid-cols-2 gap-2">
          {AVAILABLE_EVENTS.map((ev) => (
            <label
              key={ev.id}
              className={`flex items-start gap-2 p-3 border rounded-lg cursor-pointer transition-all ${
                events.includes(ev.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="checkbox"
                checked={events.includes(ev.id)}
                onChange={() => toggleEvent(ev.id)}
                className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <p className="text-sm font-medium text-gray-800">{ev.label}</p>
                <p className="text-xs text-gray-500">{ev.desc}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
          Cancelar
        </button>
        <button
          data-testid="webhook-create-submit"
          type="submit"
          disabled={loading}
          className="px-5 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 transition-colors"
          style={{ backgroundColor: '#003594' }}
        >
          {loading ? 'Creando...' : 'Crear Webhook'}
        </button>
      </div>
    </form>
  );
}

export default function Webhooks() {
  const [webhooks, setWebhooks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [whRes, projRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/webhooks`, { headers }),
        axios.get(`${BACKEND_URL}/api/projects`, { headers }),
      ]);
      setWebhooks(whRes.data);
      setProjects(projRes.data);
    } catch (err) {
      console.error('Error loading webhooks:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleToggle = async (webhookId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/webhooks/${webhookId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      loadData();
    } catch (err) {
      console.error('Error toggling webhook:', err);
    }
  };

  const handleDelete = async (webhookId) => {
    if (!window.confirm('¿Eliminar este webhook?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/webhooks/${webhookId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      loadData();
    } catch (err) {
      console.error('Error deleting webhook:', err);
    }
  };

  const handleTest = async (webhookId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/webhooks/${webhookId}/test`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      console.error('Error testing webhook:', err);
    }
  };

  return (
    <Layout>
      <div data-testid="webhooks-page" className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#051C2C' }}>Webhooks</h1>
            <p className="text-sm text-gray-500 mt-1">Recibe notificaciones cuando ocurren eventos en tus órdenes</p>
          </div>
          {!showCreate && projects.length > 0 && (
            <button
              data-testid="create-webhook-btn"
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
              style={{ backgroundColor: '#003594' }}
            >
              <Plus className="w-4 h-4" />
              Nuevo Webhook
            </button>
          )}
        </div>

        {/* Create Form */}
        {showCreate && (
          <CreateWebhookForm
            projects={projects}
            onCreated={() => { setShowCreate(false); loadData(); }}
            onCancel={() => setShowCreate(false)}
          />
        )}

        {/* Webhooks List */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Cargando webhooks...</div>
        ) : webhooks.length === 0 && !showCreate ? (
          <div className="text-center py-16 bg-white border border-gray-200 rounded-xl">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Send className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Sin webhooks configurados</h3>
            <p className="text-sm text-gray-500 mb-4 max-w-sm mx-auto">
              Configura webhooks para recibir notificaciones en tu servidor cuando se paguen, cancelen o expiren órdenes.
            </p>
            {projects.length > 0 ? (
              <button
                onClick={() => setShowCreate(true)}
                className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white rounded-lg"
                style={{ backgroundColor: '#003594' }}
              >
                <Plus className="w-4 h-4" />
                Crear Primer Webhook
              </button>
            ) : (
              <p className="text-sm text-orange-600">Crea un proyecto primero para configurar webhooks</p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {webhooks.map((wh) => (
              <WebhookCard
                key={wh.webhook_id}
                webhook={wh}
                onToggle={handleToggle}
                onDelete={handleDelete}
                onTest={handleTest}
              />
            ))}
          </div>
        )}

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">Como funcionan los webhooks</h4>
          <div className="text-xs text-blue-800 space-y-1.5">
            <p>Caura Pay envia un POST a tu URL con los datos del evento cuando ocurre una accion.</p>
            <p>Cada entrega incluye un header <code className="bg-blue-100 px-1 rounded">X-Caura-Signature</code> con una firma HMAC-SHA256 usando tu secret key para verificar autenticidad.</p>
            <p>Si tu servidor no responde con 2xx, se reintenta hasta 3 veces con backoff (0s, 5s, 30s).</p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
